import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Mail, Trash2, Plus, Shield, UserCheck, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getUsers, registerUser, deleteUser, updateUser, getContacts, updateContactStatus, deleteContact, getClients } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Admin', icon: Shield, color: 'bg-red-50 text-red-700' },
  { value: 'team', label: 'Team', icon: UserCheck, color: 'bg-sky-50 text-sky-700' },
  { value: 'client', label: 'Client', icon: Smartphone, color: 'bg-teal-50 text-teal-700' },
];

const CONTACT_STATUS = [
  { value: 'neu', label: 'Neu', color: 'bg-sky-50 text-sky-700' },
  { value: 'kontaktiert', label: 'Kontaktiert', color: 'bg-amber-50 text-amber-700' },
  { value: 'qualifiziert', label: 'Qualifiziert', color: 'bg-teal-50 text-teal-700' },
  { value: 'abgeschlossen', label: 'Abgeschlossen', color: 'bg-emerald-50 text-emerald-700' },
];

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function AdminSettingsPage() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'team', client_id: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [uRes, cRes, clRes] = await Promise.all([
        getUsers(token),
        getContacts(),
        getClients()
      ]);
      setUsers(uRes.data);
      setContacts(cRes.data);
      setClients(clRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleCreateUser = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    try {
      await registerUser(form);
      toast.success('Benutzer erstellt');
      setDialogOpen(false);
      setForm({ email: '', password: '', name: '', role: 'team', client_id: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;
    try { await deleteUser(id, token); toast.success('Gelöscht'); loadData(); } catch (err) { toast.error('Fehler'); }
  };

  const handleContactStatusChange = async (id, newStatus) => {
    try {
      await updateContactStatus(id, newStatus);
      toast.success('Status aktualisiert');
      loadData();
    } catch (err) { toast.error('Fehler'); }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Kontakt löschen?')) return;
    try { await deleteContact(id); toast.success('Gelöscht'); loadData(); } catch (err) { toast.error('Fehler'); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Einstellungen" description="Benutzerverwaltung und Kontakt-Anfragen" />

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="users" className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Benutzer ({users.length})</TabsTrigger>
          <TabsTrigger value="contacts" className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Kontakt-Anfragen ({contacts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <motion.div variants={fadeIn}>
            <div className="flex justify-end mb-4">
              <Button onClick={() => setDialogOpen(true)} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="settings-create-user-button">
                <Plus className="w-4 h-4 mr-2" /> Neuer Benutzer
              </Button>
            </div>
            {users.length === 0 ? (
              <EmptyState title="Keine Benutzer" description="Erstellen Sie den ersten Benutzer." />
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="users-table">
                  <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left p-3 pl-4 font-medium text-slate-500">Name</th>
                    <th className="text-left p-3 font-medium text-slate-500">E-Mail</th>
                    <th className="text-left p-3 font-medium text-slate-500">Rolle</th>
                    <th className="text-left p-3 font-medium text-slate-500">Erstellt</th>
                    <th className="text-right p-3 pr-4 font-medium text-slate-500">Aktion</th>
                  </tr></thead>
                  <tbody>
                    {users.map((u, i) => {
                      const roleInfo = ROLES.find(r => r.value === u.role) || ROLES[1];
                      return (
                        <tr key={u.id || i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                          <td className="p-3 pl-4 font-medium text-slate-900">{u.name}</td>
                          <td className="p-3 text-slate-700">{u.email}</td>
                          <td className="p-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleInfo.color}`}>{roleInfo.label}</span></td>
                          <td className="p-3 text-slate-500 text-xs">{u.created_at?.slice(0, 10)}</td>
                          <td className="p-3 pr-4 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </TabsContent>

        <TabsContent value="contacts">
          <motion.div variants={fadeIn}>
            {contacts.length === 0 ? (
              <EmptyState title="Keine Anfragen" description="Noch keine Kontakt-Anfragen über die Landing Page eingegangen." />
            ) : (
              <div className="space-y-3">
                {contacts.map((c, i) => {
                  const statusInfo = CONTACT_STATUS.find(s => s.value === c.status) || CONTACT_STATUS[0];
                  return (
                    <div key={c.id || i} className="kpi-card-hover rounded-xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">{c.first_name} {c.last_name}</h4>
                          <p className="text-xs text-slate-500">{c.email} {c.app_name ? `| App: ${c.app_name}` : ''} {c.budget ? `| Budget: ${c.budget}` : ''}</p>
                          {c.message && <p className="text-xs text-slate-600 mt-1 bg-slate-50 p-2 rounded-lg">{c.message}</p>}
                          <p className="text-[10px] text-slate-400 mt-1">Eingegangen: {c.created_at?.slice(0, 10)}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Select value={c.status} onValueChange={v => handleContactStatusChange(c.id, v)}>
                            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{CONTACT_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(c.id)} className="text-red-500">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Neuer Benutzer</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Vorname Nachname" data-testid="user-form-name" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">E-Mail *</label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@beispiel.de" data-testid="user-form-email" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Passwort *</label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Min. 6 Zeichen" data-testid="user-form-password" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Rolle</label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}><SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
              </div>
              {form.role === 'client' && (
                <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Zugeordneter Kunde</label>
                  <Select value={form.client_id || 'none'} onValueChange={v => setForm({...form, client_id: v === 'none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Kunde" /></SelectTrigger><SelectContent><SelectItem value="none">Keiner</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleCreateUser} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="user-form-save">Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
